// Job state and cache utilities
import { formatInTimeZone } from 'date-fns-tz';
import { toast } from 'react-hot-toast';
import { GET_JOBS_FOR_ASSIGNMENTS } from '../graphql/queries';

/**
 * Updates a job in the Apollo cache without requiring a full refetch
 * @param {Object} updatedJob - The updated job data
 * @param {Object} jobsData - Current jobs data from Apollo cache
 * @param {Object} client - Apollo client instance
 * @param {Date} selectedDate - Currently selected date
 */
export const updateJobInLocalState = (updatedJob, jobsData, client, selectedDate) => {
  if (!jobsData?.jobsForAssignments?.items || !updatedJob?.id) return;
  
  try {
    // Read the current query data from the cache
    const existingData = client.readQuery({
      query: GET_JOBS_FOR_ASSIGNMENTS,
      variables: {
        input: {
          startDate: formatInTimeZone(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()),
            'America/New_York',
            'yyyy-MM-dd\'T\'00:00:00.000XXX'
          ),
          endDate: formatInTimeZone(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59),
            'America/New_York',
            'yyyy-MM-dd\'T\'23:59:59.999XXX'
          ),
          limit: 100,
          offset: 0
        }
      }
    });
    
    if (!existingData?.jobsForAssignments) return;
    
    // Check if this update might change assignment status
    const oldJob = existingData.jobsForAssignments.items.find(job => job.id === updatedJob.id);
    const wasAssigned = oldJob?.assignedTo !== null;
    const isNowAssigned = updatedJob.assignedTo !== null;
    
    // Calculate changes to assignment count
    let assignedChange = 0;
    let unassignedChange = 0;
    
    if (!wasAssigned && isNowAssigned) {
      // Job went from unassigned to assigned
      assignedChange = 1;
      unassignedChange = -1;
    } else if (wasAssigned && !isNowAssigned) {
      // Job went from assigned to unassigned
      assignedChange = -1;
      unassignedChange = 1;
    }
  
    // Create a new array with the updated job
    const updatedItems = existingData.jobsForAssignments.items.map((job) => {
      if (job.id === updatedJob.id) {
        // Merge the updated job with the existing job data
        // Preserve nested objects that might not be in the update
        return {
          ...job,
          ...updatedJob,
          property: updatedJob.property || job.property,
          reservation: updatedJob.reservation || job.reservation,
          assignedTo: updatedJob.assignedTo || job.assignedTo,
          serviceType: updatedJob.serviceType || job.serviceType
        };
      }
      return job;
    });
    
    // Update the local cache with the new data
    client.writeQuery({
      query: GET_JOBS_FOR_ASSIGNMENTS,
      variables: {
        input: {
          startDate: formatInTimeZone(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()),
            'America/New_York',
            'yyyy-MM-dd\'T\'00:00:00.000XXX'
          ),
          endDate: formatInTimeZone(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59),
            'America/New_York',
            'yyyy-MM-dd\'T\'23:59:59.999XXX'
          ),
          limit: 100,
          offset: 0
        }
      },
      data: {
        jobsForAssignments: {
          ...existingData.jobsForAssignments,
          items: updatedItems,
          assigned: (existingData.jobsForAssignments.assigned || 0) + assignedChange,
          unassigned: (existingData.jobsForAssignments.unassigned || 0) + unassignedChange
        }
      }
    });
    
    return {
      assignedChange, 
      unassignedChange
    };
  } catch (e) {
    console.error('Error updating job in local state:', e);
    return null;
  }
};

/**
 * Validates job data and makes sure it has all required properties
 * @param {Object} job - The job data to check
 * @param {Object} jobsData - Current jobs data from Apollo cache
 * @param {Object} propertiesData - Properties data from Apollo
 * @returns {Object|null} - Fixed job object or null if unfixable
 */
export const checkAndFixJobData = (job, jobsData, propertiesData) => {
  if (!job) return null;
  
  // If job has propertyId but missing property object
  if (job.propertyId && (!job.property || !job.property.name)) {
    // Try to find the property from existing jobs
    const existingJobs = jobsData?.jobsForAssignments?.items || [];
    const matchingProperty = existingJobs.find(j => j.property?.id === job.propertyId)?.property;
    
    if (matchingProperty) {
      return {
        ...job,
        property: matchingProperty
      };
    }
    
    // Or try to find the property from properties data
    const properties = propertiesData?.propertiesWithReservations?.properties || [];
    const matchingPropertyFromList = properties.find(p => p.id === job.propertyId);
    
    if (matchingPropertyFromList) {
      return {
        ...job,
        property: matchingPropertyFromList
      };
    }
  }
  
  return job;
};

/**
 * Adds a new job to the cache
 * @param {Object} newJob - The new job to add
 * @param {Object} jobsData - Current jobs data from Apollo cache
 * @param {Object} propertiesData - Properties data from Apollo
 * @param {Object} client - Apollo client instance
 * @param {Date} selectedDate - Currently selected date
 * @param {Function} showNotification - Function to show notification
 * @param {Function} refetchJobs - Function to refetch jobs if cache update fails
 */
export const handleAddNewJob = async (newJob, jobsData, propertiesData, client, selectedDate, showNotification, refetchJobs) => {
  if (!newJob || !jobsData?.jobsForAssignments?.items) {
    return;
  }
  
  if (!newJob.property || !newJob.property.id) {
    // If we have propertyId but not property object, try to find the property from existing data
    if (newJob.propertyId) {
      const existingProperty = jobsData.jobsForAssignments.items
        .find(job => job.property?.id === newJob.propertyId)?.property;
        
      if (existingProperty) {
        newJob.property = existingProperty;
      } else {
        // Try to find from properties data
        const propertyFromList = propertiesData?.propertiesWithReservations?.properties
          ?.find(p => p.id === newJob.propertyId);
          
        if (propertyFromList) {
          newJob.property = propertyFromList;
        } else {
          // If we still can't find property info, request fresh data
          refetchJobs();
          return;
        }
      }
    } else {
      refetchJobs();
      return;
    }
  }
  
  // Ensure the job object has all fields required by the cache schema
  const completeJob = {
    __typename: "Job",
    id: newJob.id || `temp-${Date.now()}`, // Temporary ID if none provided
    ...newJob,
    cleaningType: newJob.cleaningType || (newJob.category === 'CLEANING' ? 'STANDARD' : null),
    isOwnerArrival: newJob.isOwnerArrival || false,
    isOwnerDeparture: newJob.isOwnerDeparture || false,
    isMaintenanceStart: newJob.isMaintenanceStart || false,
    isMaintenanceEnding: newJob.isMaintenanceEnding || false,
    reservationId: newJob.reservationId || null,
    reservation: newJob.reservation || null,
    assignedTo: newJob.assignedTo || null,
    serviceType: newJob.serviceType || null,
    statusHistory: newJob.statusHistory || [],
    isCompleted: newJob.isCompleted || false,
    createdAt: newJob.createdAt || new Date().toISOString(),
    updatedAt: newJob.updatedAt || new Date().toISOString()
  };

  // Ensure property has required fields
  if (completeJob.property) {
    completeJob.property = {
      __typename: "Property",
      ...completeJob.property,
      checkInTime: completeJob.property.checkInTime || '15:00',
      city: completeJob.property.city || '',
      state: completeJob.property.state || '',
      zip: completeJob.property.zip || '',
      propertyCode: completeJob.property.propertyCode || ''
    };
  }
  
  try {
    // Read current cache data
    const existingData = client.readQuery({
      query: GET_JOBS_FOR_ASSIGNMENTS,
      variables: {
        input: {
          startDate: formatInTimeZone(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()),
            'America/New_York',
            'yyyy-MM-dd\'T\'00:00:00.000XXX'
          ),
          endDate: formatInTimeZone(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59),
            'America/New_York',
            'yyyy-MM-dd\'T\'23:59:59.999XXX'
          ),
          limit: 100,
          offset: 0
        }
      }
    });
    
    if (!existingData?.jobsForAssignments) {
      // If no cache data, fall back to refetch
      await refetchJobs();
      return;
    }
    
    // Create a new array with the new complete job
    const updatedItems = [...existingData.jobsForAssignments.items, completeJob];
    
    // Update the cache directly
    client.writeQuery({
      query: GET_JOBS_FOR_ASSIGNMENTS,
      variables: {
        input: {
          startDate: formatInTimeZone(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()),
            'America/New_York',
            'yyyy-MM-dd\'T\'00:00:00.000XXX'
          ),
          endDate: formatInTimeZone(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59),
            'America/New_York',
            'yyyy-MM-dd\'T\'23:59:59.999XXX'
          ),
          limit: 100,
          offset: 0
        }
      },
      data: {
        jobsForAssignments: {
          ...existingData.jobsForAssignments,
          items: updatedItems,
          total: (existingData.jobsForAssignments.total || 0) + 1,
          unassigned: (existingData.jobsForAssignments.unassigned || 0) + 1, // New job is unassigned
        }
      }
    });
    
    // Show success notification
    showNotification(`New job created for ${completeJob.property?.name || 'property'}`, 'success');
    
    return { 
      total: 1, 
      unassigned: 1 
    };
  } catch (e) {
    console.error('Error updating cache with new job:', e);
    // Fall back to refetch if cache update fails
    await refetchJobs();
    return null;
  }
};

/**
 * Wrapper function that checks job data before adding
 * @param {Object} newJob - The new job to add
 * @param {Object} jobsData - Current jobs data from Apollo cache
 * @param {Object} propertiesData - Properties data from Apollo 
 * @param {Object} jobCounts - Current job counts
 * @param {Object} totalJobsCount - Total job counts from backend
 * @param {Object} client - Apollo client instance
 * @param {Date} selectedDate - Currently selected date
 * @param {Function} showNotification - Function to show notification
 * @param {Function} refetchJobs - Function to refetch jobs if cache update fails
 * @param {Function} setJobCounts - Function to update job counts in state
 */
export const handleAddNewJobWithCheck = (newJob, jobsData, propertiesData, jobCounts, totalJobsCount, client, selectedDate, showNotification, refetchJobs, setJobCounts) => {
  // Initialize job counts if they're not set yet
  if (jobCounts.total === 0 && totalJobsCount) {
    setJobCounts({
      total: totalJobsCount.total || 0,
      assigned: totalJobsCount.assigned || 0,
      unassigned: totalJobsCount.unassigned || 0
    });
  }
  
  const fixedJob = checkAndFixJobData(newJob, jobsData, propertiesData);
  
  if (fixedJob) {
    handleAddNewJob(fixedJob, jobsData, propertiesData, client, selectedDate, showNotification, refetchJobs)
      .then(result => {
        if (result) {
          // Update job counts
          setJobCounts(prev => ({
            ...prev,
            unassigned: prev.unassigned + result.unassigned,
            total: prev.total + result.total
          }));
        }
      });
  } else {
    refetchJobs();
  }
};

/**
 * Helper function to format date params for queries
 * @param {Date} date - Date to format
 * @returns {Object} - Formatted date range
 */
export const getFormattedDateRange = (date) => {
  return {
    startDate: formatInTimeZone(
      new Date(date.getFullYear(), date.getMonth(), date.getDate()),
      'America/New_York',
      'yyyy-MM-dd\'T\'00:00:00.000XXX'
    ),
    endDate: formatInTimeZone(
      new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59),
      'America/New_York',
      'yyyy-MM-dd\'T\'23:59:59.999XXX'
    )
  };
}; 